<template>
  <div>
    <h2>Devices</h2>
    <div class="row justify-content-between align-items-end" v-if="is_admin">
      <div class="col-12 col-md-6 mb-1">
        <router-link :to="{ name: 'DevicesAdd' }" class="btn btn-success"
          ><i class="fa fa-edit" aria-hidden="true"></i> Add Device
        </router-link>
        <button class="btn btn-info mx-1" @click="refreshDevices()">
          <i class="fa fa-sync"></i> Refresh
        </button>
        <button class="btn btn-light" @click="createExportFile()">
          <i class="fa fa-file"></i> Export All
        </button>
      </div>
      <div class="col-12 col-md-6">
        <div class="d-flex align-items-end mb-1">
          <label class="w-25 text-right">Search:&nbsp;</label>
          <div class="w-75">
            <input 
              class="w-100 form-control"
              style="border-radius: 4px"
              v-model="device_query_text"
              maxlength="100"
              placeholder="Search by device name, ip address, or extension"
            />
          </div>
        </div>
        <div class="d-flex align-items-end my-1">
          <label class="w-25 text-right">Type:&nbsp;</label>
          <div class="w-75">
            <v-select
              multiple
              v-model="device_types_selected_ids"
              :options="device_types_for_dropdown"
              :reduce="(device_type) => device_type.value"
              placeholder="Showing All"
            ></v-select>
          </div>
        </div>
        <div class="d-flex align-items-end my-1" v-if="includesSip(device_types_selected_ids)">
          <label class="w-25 text-right">Sip Type:&nbsp;</label>
          <div class="w-75">
            <v-select
              multiple
              v-model="device_sip_types_selected_ids"
              :options="device_sip_types_for_dropdown"
              :reduce="(device_sip_type) => device_sip_type.value"
              placeholder="Showing All"
            ></v-select>
          </div>
        </div>
        <div class="d-flex align-items-end my-1" v-if="includesSubtypes(device_types_selected_ids)">
          <label class="w-25 text-right">Subtype:&nbsp;</label>
          <div class="w-75">
            <v-select
              multiple
              v-model="device_subtypes_selected_ids"
              :options="device_subtypes_for_dropdown"
              :reduce="(device_subtype) => device_subtype.value"
              placeholder="Showing All"
            ></v-select>
          </div>
        </div>
      </div>
    </div>
    <br />
    <div class="row">
      <div class="col-12">
        <table v-if="filtered_devices.length" class="table table-striped">
          <thead class="bg-hub-primary text-white">
            <tr>
              <th v-if="machine_type === 'Box'" style="width: 2%"></th>
              <th style="width: 20%">
                <a @click="sortDevices('name')">Name</a>
              </th>
              <th v-if="machine_type === 'Box'"></th> <!-- G2 IP Camera enabled for box machines -->
              <th class="d-none d-md-table-cell" style="width: 25%">
                <a @click="sortDevices('device_type.name')">Type</a>
              </th>
              <th class="d-none d-md-table-cell" style="width: 25%">
                Zones
              </th>
              <th class="d-none d-md-table-cell" style="width: 10%">
                <a @click="sortDevices('ip')">IP</a>
              </th>
              <th style="width: 10%">
                <a @click="sortDevices('extension')">Extension</a>
              </th>
              <th style="width: 5%">Manage</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="device in filtered_devices" :key="device.device_id">
              <td v-if="machine_type === 'Box' && device.online">
                <i class="far fa-check-circle online" aria-hidden="true"></i>
              </td>
              <td v-else-if="machine_type === 'Box' && !device.online">
                <i class="far fa-times-circle offline" aria-hidden="true"></i>
              </td>
              <td>
                <!-- Haven lock status -->
                <template v-if="isHavenLock(device)">
                  <span v-html="getBatteryIcon(device.device_id)"></span>
                  <span v-if="isJammed(device.device_id)"><i style="color: red;" class="fa-solid fa-circle-exclamation fa-lg" title="The lock is jammed."></i>&nbsp;</span>
                  <span v-if="isUnlocked(device.device_id)"><i style="color: orange;" class="fa fa-unlock fa-lg" title="The lock is unlocked."></i>&nbsp;</span>
                  <span v-if="isLocked(device.device_id)"><i style="color: green;" class="fa fa-lock fa-lg" title="The lock is locked."></i>&nbsp;</span>
                  <span v-if="isUnknown(device.device_id)"><i class="fa fa-circle-question fa-lg" title="The lock status is unknown."></i>&nbsp;</span>
                </template>
                <template v-if="machine_type === 'Box' && is_admin && isLinkDevice(device)">
                  <router-link :to="{ name: 'DeviceDetailsG2Link', params: { device_id: device.device_id } }">
                    {{ device.name }}
                  </router-link>
                </template>
                <span v-else>
                  {{ device.name }}
                </span>
                <span v-if=isVisual(device)>(ID: {{device.device_id }})</span>
              </td>
              <td v-if="machine_type === 'Box'"> <!-- G2 IP Camera enabled for box machines -->
                <template v-if="isZycooCameraDevice(device)"> 
                  <router-link
                    class="dropdown-item"
                    :to="{ name: 'DeviceDetailsG2Speaker', params: { device_id: device.device_id } }"
                  >
                    <!-- Date is appended to the image URL to force a refresh and avoid browser caching issues. -->
                    <img class="video-feed-image" :src="'./upload/images/zycoo_snapshots/' + device.camera_ip + '_snapshot.jpg?d=' + Date.now()" @error="setDefaultImage($event, device.device_id)" />
                    <i style="display:none;" :id="'video-icon-' + device.device_id" class="fa-solid fa-lg fa-video"></i>
                  </router-link>
                </template>
              </td>
              <td class="d-none d-md-table-cell">
                {{ device.device_type.name }}
                <span v-if="isSip(device)">({{ device.device_sip_type.name }})</span>
                <span v-if="hasSubtype(device)">({{ device.device_subtype.name }})</span>
              </td>
              <td
                class="d-none d-md-table-cell"
                v-if="device.device_zones && device.device_zones.length > 0"
              >
                <button
                  type="button"
                  class="btn btn-light"
                  @click="toggle_zones(device.device_id)"
                >
                  {{
                    devices_showing_zones.includes(device.device_id)
                      ? "Hide"
                      : "Show"
                  }}
                  Zones
                </button>
                <div v-if="devices_showing_zones.includes(device.device_id)">
                  <br />
                  <table class="table">
                    <tbody>
                      <tr
                        v-for="device_zone in device.device_zones"
                        :key="device_zone.zone_id"
                      >
                        <td>
                          <span v-if="device_zone.site_id === 1"
                            >[{{ device_zone.site_name }}]</span
                          >
                          {{ device_zone.name }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </td>
              <td class="d-none d-md-table-cell" v-else>
                No zones assigned
              </td>

              <td
                class="d-none d-md-table-cell"
                v-if="hasIpAccess(device)"
              >
                <a :href="'http://' + device.ip" target="_blank">{{
                  device.ip
                }}</a>
              </td>
              <td class="d-none d-md-table-cell" v-else>N/A</td>
              <td v-if="hasExtension(device)">
                <span v-if="device.registered" class="registered"
                  >{{ device.extension }}
                  <i class="fa fa-phone fa-fw" aria-hidden="true"></i
                ></span>
                <span class="not-registered" v-else
                  >{{ device.extension }}
                  <i class="fa fa-phone-slash fa-fw" aria-hidden="true"></i
                ></span>
              </td>
              <td v-else>N/A</td>
              <td>
                <div class="dropdown">
                  <button
                    class="btn btn-light dropdown-toggle"
                    type="button"
                    id="dropdownMenuButton"
                    data-toggle="dropdown"
                    aria-haspopup="true"
                    aria-expanded="false"
                  >
                    <i class="fa fa-cogs fa-fw" aria-hidden="true"></i>
                  </button>
                  <div
                    class="dropdown-menu dropdown-menu-right"
                    aria-labelledby="dropdownMenuButton"
                  >
                    <router-link
                      :to="{
                        name: 'DevicesEdit',
                        params: { device_id: device.device_id },
                      }"
                      class="dropdown-item"
                      v-if="is_admin"
                      ><i class="fa fa-pencil-alt fa-fw" aria-hidden="true"></i
                      >&nbsp;Edit
                    </router-link>
                    <a
                      class="dropdown-item"
                      v-if="isHavenLock(device) && isUnlocked(device.device_id, true)"
                      href="#"
                      onclick="return false"
                      @click="lockHavenLock(device.device_id)"
                      ><i class="fa fa-lock fa-fw" aria-hidden="true"></i
                      >&nbsp;Lock</a
                    >
                    <a
                      class="dropdown-item"
                      v-if="isHavenLock(device) && isLocked(device.device_id, true)"
                      href="#"
                      onclick="return false"
                      @click="unlockHavenLock(device.device_id)"
                      ><i class="fa fa-unlock fa-fw" aria-hidden="true"></i
                      >&nbsp;Unlock</a
                    >
                    <a
                      class="dropdown-item"
                      href="#"
                      onclick="return false"
                      v-if="
                        device.extension !== 9000 && isSip(device)
                      "
                      @click="call(device.extension)"
                    >
                      <i class="fa fa-phone fa-fw" aria-hidden="true"></i
                      >&nbsp;Call
                    </a>
                    <a
                      class="dropdown-item"
                      href="#"
                      onclick="return false"
                      @click="deleteDevice(device)"
                      v-if="is_admin"
                      ><i class="fa fa-trash fa-fw" aria-hidden="true"></i
                      >&nbsp;Delete</a
                    >
                    <div
                      class="dropdown-divider"
                      v-if="[2, 3, 4].includes(logged_in_user.group_id) && !isHavenLock(device)"
                    ></div>
                    <div v-if="isAND(device)">
                      <a
                        class="dropdown-item"
                        :href="
                          'http://' +
                          device.ip +
                          '/status?password=' +
                          getANDCommandPassword(device)
                        "
                        target="_blank"
                        >Status</a
                      >
                      <div v-if="is_admin">
                        <a
                          class="dropdown-item"
                          :href="
                            'http://' +
                            device.ip +
                            '/reboot?password=' +
                            getANDCommandPassword(device)
                          "
                          target="_blank"
                          >Reboot</a
                        >
                        <a
                          class="dropdown-item"
                          :href="
                            'http://' +
                            device.ip +
                            '/config_all?password=' +
                            getANDCommandPassword(device)
                          "
                          target="_blank"
                          >Config All</a
                        >
                      </div>
                    </div>
                    <div v-else>
                      <div v-if="hasIpManage(device)">
                        <a
                          class="dropdown-item"
                          :href="'http://' + device.ip + '/#status'"
                          target="_blank"
                          >Status</a
                        >
                        <div v-if="is_admin">
                          <a
                            class="dropdown-item"
                            :href="'http://' + device.ip + '/#network'"
                            target="_blank"
                            >Network</a
                          >
                          <a
                            class="dropdown-item"
                            :href="'http://' + device.ip + '/#update'"
                            target="_blank"
                            >Update Page</a
                          >
                          <a
                            class="dropdown-item"
                            :href="'http://' + device.ip + '/update.html'"
                            target="_blank"
                            >Update State</a
                          >
                          <a
                            class="dropdown-item"
                            :href="
                              'http://' +
                              device.ip +
                              '/rc.cgi?L=uirreboot.html&c=99'
                            "
                            target="_blank"
                            >Reboot</a
                          >
                        </div>
                      </div>
                      <div v-else>
                        <a
                          class="dropdown-item"
                          v-if="!isHavenLock(device)"
                          :href="'http://' + device.ip"
                          target="_blank"
                          >Click to Access</a
                        >
                        <router-link
                          v-if="machine_type === 'Box' && is_admin && isLinkDevice(device)"
                          class="dropdown-item"
                          :to="{ name: 'DeviceDetailsG2Link', params: { device_id: device.device_id } }"
                        >
                          Configure
                        </router-link>
                      </div>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-else class="d-flex justify-content-center">
          <b>Sorry, there are no results <span v-if="device_query_text.trim() !== ''">for "{{ device_query_text }}"</span></b>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-2">
        Total: {{ device_total }} <br />
        Registered: {{ device_registered_count }} / {{ device_sip_total }}
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'
import HavenLockMixin from '@/mixins/HavenLockMixin'
import DeviceListMixin from '../../mixins/DeviceListMixin.vue'
import { DEVICE_SIP_TYPE, DEVICE_SUBTYPE, DEVICE_TYPE } from '../../assets/js/resource-types'

export default {
  components: { vSelect },
  name: 'devices',
  data: function () {
    return {
      devices_showing_zones: []
    }
  },
  mixins: [HavenLockMixin, DeviceListMixin],
  computed: {
    ...mapGetters({
      device_types_for_dropdown: 'allDeviceTypesForDropdown',
      device_sip_types_for_dropdown: 'allDeviceSipTypesForDropdown',
      logged_in_user: 'loggedInUser',
      machine_type: 'machineType',
    }),
    is_admin: function () {
      return [2, 3, 4].includes(this.logged_in_user.group_id)
    }
  },
  methods: {
    ...mapActions(['deleteDevice']),
    isSip(device) {
      return device.device_type_id === DEVICE_TYPE.SIP
    },
    isVisual(device) {
      return device.device_type_id === DEVICE_TYPE.Visual
    },
    isAND(device) {
      return this.isSip(device) && device.device_sip_type_id === DEVICE_SIP_TYPE.Advanced
    },
    isHavenLock(device) {
      return device.device_type_id === DEVICE_TYPE.HAVEN
    },
    isLinkDevice(device) {
      return device.device_type_id === DEVICE_TYPE.G2Link
    },
    isZycooCameraDevice(device) {
      return device.device_type_id === DEVICE_TYPE.G2IPSpeaker && device.device_subtype_id === DEVICE_SUBTYPE.ClockCamera
    },
    includesSip(device_type_ids) {
      return device_type_ids.includes(DEVICE_TYPE.SIP)
    },
    includesSubtypes(device_type_ids) {
      for(let device_type_id of device_type_ids) {
        const device_type = this.filtered_device_types.find(dt => dt.device_type_id === device_type_id)
        if(device_type && device_type.subtypes.length > 0) {
          return true
        }
      }
      return false
    },
    hasIpAccess(device) {
      return ![
        DEVICE_TYPE.Visual,
        DEVICE_TYPE.HAVEN
      ].includes(device.device_type_id)
    },
    hasIpManage(device) {
      return this.isSip(device) && [
        DEVICE_SIP_TYPE.HUB,
        DEVICE_SIP_TYPE.MS450,
        DEVICE_SIP_TYPE.MS250
      ].includes(device.device_sip_type_id)
    },
    hasExtension(device) {
      return device.extension !== null && device.extension !== '' && device.extension !== 0
    },
    hasSubtype(device) {
      return device.device_subtype
    },
    createExportFile () {
      let fileExport = {
        type: 'device'
      }

      this.$store.dispatch('createExportFile', fileExport)
    },
    call (extension) {
      this.$store.dispatch(
        'executeCommand',
        'start_intercom_call ' + this.scoped_site_id + ' ' + extension
      )
    },
    getANDCommandPassword (device) {
      return device.device_attributes.filter(
        (deviceAttribute) => deviceAttribute.attr_name === 'Command Password'
      )[0].attr_str
    },
    toggle_zones (deviceId) {
      if (this.devices_showing_zones.includes(deviceId)) {
        this.devices_showing_zones.splice(
          this.devices_showing_zones.indexOf(deviceId),
          1
        )
      } else {
        this.devices_showing_zones.push(deviceId)
      }
    },
    setDefaultImage(event, device_id) {
      event.target.remove();
      const img = document.getElementById("video-icon-" + device_id);
      img.style.display = "block";
    },
  }
}
</script>

<style scoped>
.online {
  color: green;
}

.offline {
  color: red;
}

.registered {
  font-weight: bold;
  color: green;
}

.not-registered {
  font-weight: bold;
  color: red;
}

.video-feed-image {
  height: auto;
  max-width: 90px;
  max-height: 60px;
}

thead > tr > th > a:hover {
  cursor: pointer;
}
</style>
